import type { PagedModelClientPost } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { InfiniteData } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevinePostApi } from '@/features/api/hooks/grapevine-post/useGrapevinePostApi';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { getPostProps } from '@/features/posts/utils/post';

export const useGetEmergencyPosts = (params: {
  radiusKm: number;
  homeAreaId?: string;
  count?: number;
  enabled?: boolean;
}) => {
  const { isAuthenticated } = useAuth();
  const api = useGrapevinePostApi();
  const { homeAreaId, radiusKm } = params;
  const count = params.count ?? 10;
  const enabled = params.enabled ?? true;
  return useInfiniteQuery({
    queryKey: [
      API_CACHE_KEYS.EMERGENCY_POST_FEED,
      { homeAreaId, radiusKm, count }
    ],
    queryFn: ({ pageParam }) =>
      api.getEmergencyPosts({
        radiusKm,
        page: pageParam,
        count
      }),
    enabled: isAuthenticated && enabled,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    select: useCallback(
      (data: InfiniteData<PagedModelClientPost, number>) =>
        data.pages
          .flatMap((page) => page.content?.map((post) => getPostProps(post)))
          .filter((p) => p !== undefined),
      []
    ),
    getNextPageParam: (lastPage) =>
      (lastPage.page?.totalElements ?? 0) >
      ((lastPage.page?.number ?? 0) + 1) * (lastPage.page?.size ?? 10)
        ? (lastPage.page?.number ?? 0) + 1
        : undefined
  });
};
